import {useEffect,useRef,useState} from 'react';
import {hold,release} from '@/lib/unsavedChanges';

// EXEC240: the fixed-asset and borrowing panels edit a whole schedule locally and save it through the KKA editor's queue
// (onSave = saveDoc). While the panel has edits it has not saved, it holds the shared unsaved guard under its own token,
// so a successful write elsewhere on the page (another field, evidence) does not release it. A server value that changes
// while the panel is clean is shown; one that changes while the panel is dirty is announced and the user's edits are kept.
const key=v=>JSON.stringify(v??null);
const EMPTY_REF=r=>r&&typeof r==='object'&&!Array.isArray(r)&&Object.values(r).every(v=>v===''||v==null);
// Source references the user opened but left empty are not sent: an empty reference is no reference.
export function prune(value){
 if(Array.isArray(value))return value.map(prune);
 if(!value||typeof value!=='object')return value;
 const out={};
 for(const [k,v] of Object.entries(value)){
  if(['ref','client_journal'].includes(k)&&EMPTY_REF(v))continue;
  if(k==='sources'&&v&&typeof v==='object'){const kept=Object.fromEntries(Object.entries(v).filter(([,r])=>!EMPTY_REF(r)));if(Object.keys(kept).length)out[k]=kept;continue;}
  out[k]=prune(v);
 }
 return out;
}

// The base is the stored schedule the user's edits were made on. It stays put while the panel is dirty: a schedule saved
// meanwhile by someone else (seen when the editor reloads the paper) is not a new base for those edits, and a normal save
// would write the whole stale schedule over it. Saving is therefore held until the user explicitly loads the stored
// version; the editor also receives the base, so a newer paper applied while the save waits in its queue is a conflict.
export default function useScheduleDraft(document,field,onSave){
 const server=document[field]??null,serverKey=key(server);
 const [draft,setDraftState]=useState(server),[dirty,setDirtyState]=useState(false),[changedElsewhere,setChangedElsewhere]=useState(false),[saving,setSaving]=useState(false);
 const draftRef=useRef(server),dirtyRef=useRef(false),base=useRef(serverKey),latest=useRef(serverKey),paper=useRef(document.id),sent=useRef(null),owner=useRef({});
 latest.current=serverKey;
 const setDraft=v=>{draftRef.current=v;setDraftState(v);};
 const setDirty=v=>{dirtyRef.current=v;setDirtyState(v);};
 useEffect(()=>{
  if(paper.current!==document.id){paper.current=document.id;base.current=serverKey;setDraft(server);setDirty(false);setChangedElsewhere(false);return;}
  if(serverKey===sent.current){base.current=serverKey;setChangedElsewhere(false);return;}   // this panel's own save coming back
  if(!dirtyRef.current){base.current=serverKey;setDraft(server);setChangedElsewhere(false);return;}
  setChangedElsewhere(serverKey!==base.current);
 // eslint-disable-next-line react-hooks/exhaustive-deps
 },[document.id,serverKey]);
 useEffect(()=>{if(dirty)hold(owner.current);else release(owner.current);},[dirty]);
 useEffect(()=>{const mine=owner.current;return()=>release(mine);},[]);
 const change=next=>{setDraft(typeof next==='function'?next(draftRef.current):next);setDirty(true);};
 const save=async()=>{
  if(latest.current!==base.current){setChangedElsewhere(true);return {error:'changed-elsewhere'};}   // never over a newer schedule
  const value=prune(draftRef.current),body=key(value);
  sent.current=body;setSaving(true);
  try{
   const result=await onSave({[field]:value},{[field]:JSON.parse(base.current)});
   if(result?.error)return result;                  // refused: the edits stay and the guard stays held
   // The editor applies the saved paper before the save resolves, so the stored schedule is now this panel's own write.
   if(key(prune(draftRef.current))===body){base.current=latest.current;setDirty(false);setChangedElsewhere(false);}
   return result;
  }finally{setSaving(false);}
 };
 const reload=()=>{base.current=serverKey;setDraft(server);setDirty(false);setChangedElsewhere(false);};
 return {draft,change,dirty,changedElsewhere,saving,save,reload};
}
